/**
 * micPermission
 *
 * Microphone permission plumbing for the native shell.
 *
 * iOS shows its microphone dialog EXACTLY ONCE per install. If the user taps
 * "Don't Allow" the app can never ask again; the only recovery is the Settings
 * app. That single fact drives everything here:
 *
 * - We never let the system prompt appear cold. `MicPrimer` explains first, and
 *   only the user's explicit "Allow microphone" tap triggers `requestMicAccess`.
 * - We remember the outcome, because there is no reliable permission query in
 *   WKWebView (`navigator.permissions.query({ name: 'microphone' })` is not
 *   implemented there), so the UI would otherwise have no idea what happened.
 * - A denial routes to the Settings deep link rather than a dead end.
 *
 * `requestMicAccess` also carries the OverconstrainedError retry that the rest of
 * the codebase mostly lacks (see spec 6.8): a hard constraint failure becomes a
 * successful capture with default constraints instead of a permanent error.
 *
 * On the web every export still works (it is plain getUserMedia), but nothing in
 * this module is wired into a web code path: only native-guarded callers use it.
 */

import posthog from 'posthog-js';
import { isNative, isIOSNative } from '@/lib/platform';
import { openNativeUrl } from './capacitorApp';

export const MIC_STATE = {
  UNKNOWN: 'unknown',
  GRANTED: 'granted',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
  ERROR: 'error',
};

const STORAGE_STATE = 'ttt_native_mic_state';
const STORAGE_PRIMER_SEEN = 'ttt_native_mic_primer_seen';

/** iOS deep link to this app's own Settings page. */
export const IOS_SETTINGS_URL = 'app-settings:';

function readLocal(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / quota: the flow still works, it just re-asks */
  }
}

/** Last known outcome of a microphone request, or 'unknown' if we never asked. */
export function getRememberedMicState() {
  const v = readLocal(STORAGE_STATE);
  return Object.values(MIC_STATE).includes(v) ? v : MIC_STATE.UNKNOWN;
}

export function setRememberedMicState(state) {
  writeLocal(STORAGE_STATE, state);
}

/** True once the user has been through the primer, whatever they chose. */
export function hasSeenMicPrimer() {
  return readLocal(STORAGE_PRIMER_SEEN) === '1';
}

export function markMicPrimerSeen() {
  writeLocal(STORAGE_PRIMER_SEEN, '1');
}

/**
 * The primer is worth showing only in the native shell, and only while the
 * system dialog has not been answered yet. Once iOS has an answer, showing an
 * explainer that leads to a dialog that will never appear is worse than useless.
 */
export function shouldShowMicPrimer() {
  if (!isNative()) return false;
  if (getRememberedMicState() === MIC_STATE.GRANTED) return false;
  if (hasSeenMicPrimer()) return false;
  return true;
}

function classifyError(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return MIC_STATE.DENIED;
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return MIC_STATE.UNAVAILABLE;
  }
  return MIC_STATE.ERROR;
}

/**
 * Open the microphone, with a fallback to default constraints.
 *
 * The retry is the important part: `OverconstrainedError` (and Safari's older
 * `ConstraintNotSatisfiedError`) means "I have a microphone, just not on those
 * terms". Retrying with `{ audio: true }` can only turn a hard failure into a
 * working stream, never the reverse.
 *
 * @param {MediaStreamConstraints['audio']} audioConstraints
 * @returns {Promise<MediaStream>}
 */
export async function getMicStream(audioConstraints = true) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    const err = new Error('getUserMedia unavailable');
    err.name = 'NotFoundError';
    throw err;
  }
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
  } catch (err) {
    const name = err?.name || '';
    const overconstrained = name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError';
    if (overconstrained && audioConstraints !== true) {
      return navigator.mediaDevices.getUserMedia({ audio: true });
    }
    throw err;
  }
}

/**
 * Ask for the microphone and immediately release it. On iOS this is what raises
 * the one-and-only system dialog, so it must never be called except from an
 * explicit user tap in `MicPrimer` or the Settings recovery row.
 *
 * @returns {Promise<'granted'|'denied'|'unavailable'|'error'>}
 */
export async function requestMicAccess() {
  try {
    const stream = await getMicStream(true);
    stream.getTracks().forEach((track) => track.stop());
    setRememberedMicState(MIC_STATE.GRANTED);
    try {
      posthog.capture('native_mic_permission', { result: 'granted', platform: isIOSNative() ? 'ios' : 'web' });
    } catch {}
    return MIC_STATE.GRANTED;
  } catch (err) {
    const state = classifyError(err);
    setRememberedMicState(state);
    try {
      posthog.capture('native_mic_permission', {
        result: state,
        error_name: err?.name || 'unknown',
        platform: isIOSNative() ? 'ios' : 'web',
      });
    } catch {}
    return state;
  }
}

/**
 * Send the user to the iOS Settings page for this app. The only recovery from a
 * permanent denial.
 * @returns {Promise<boolean>}
 */
export async function openMicSettings() {
  try {
    posthog.capture('native_mic_open_settings', {});
  } catch {}
  return openNativeUrl(IOS_SETTINGS_URL);
}
